import type { SyncEventInput } from '@trackeat/shared';
import { openLocalDb, type PendingOpRow } from '../db/local.js';

const TERMINAL_LEGACY_STOCK_REASONS = new Set([
  'legacy_stock_requires_reconciliation',
  'stock_command_required',
]);

const LEGACY_STOCK_KINDS = new Set([
  'stock_in',
  'stock_out',
  'stock_reserved',
]);

export interface LegacyStockReconciliation {
  client_uuid: string;
  pending_op_id: number;
  tenant_id: string;
  business_id: string | null;
  event_kind: string;
  event: SyncEventInput;
  reason: string;
  status: 'reconciliation_required' | 'resolved';
  resolution: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

function notifyChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('trackeat:legacy-stock-reconciliation'));
  }
}

export function isTerminalLegacyStockRejection(reason: string): boolean {
  const code = reason.trim().split(':', 1)[0] ?? '';
  return TERMINAL_LEGACY_STOCK_REASONS.has(code);
}

export async function quarantineLegacyStockPendingOp(input: {
  pending: PendingOpRow;
  event: SyncEventInput;
  reason: string;
  now?: string;
}): Promise<void> {
  const { pending, event, reason } = input;
  if (
    pending.kind !== 'event'
    || !LEGACY_STOCK_KINDS.has(event.kind)
    || !isTerminalLegacyStockRejection(reason)
  ) {
    throw new Error('legacy_stock_reconciliation_input_invalid');
  }
  const now = input.now ?? new Date().toISOString();
  const db = await openLocalDb(null);
  await db.executeTransaction([
    {
      statement:
        `INSERT INTO legacy_stock_reconciliations_v1(
           client_uuid, pending_op_id, tenant_id, business_id, event_kind,
           event_snapshot_json, reason, status, resolution, created_at,
           updated_at, resolved_at
         ) VALUES (?, ?, ?, ?, ?, ?, ?, 'reconciliation_required', NULL, ?, ?, NULL)
         ON CONFLICT(client_uuid) DO UPDATE SET
           pending_op_id = excluded.pending_op_id,
           tenant_id = excluded.tenant_id,
           business_id = excluded.business_id,
           event_kind = excluded.event_kind,
           event_snapshot_json = excluded.event_snapshot_json,
           reason = excluded.reason,
           status = 'reconciliation_required',
           resolution = NULL,
           updated_at = excluded.updated_at,
           resolved_at = NULL`,
      values: [
        event.client_uuid,
        pending.id,
        event.tenant_id,
        event.business_id ?? null,
        event.kind,
        JSON.stringify(event),
        reason,
        now,
        now,
      ],
    },
    {
      // La proiezione ottimistica non è mai stata accettata dal server: non
      // deve continuare a sembrare giacenza reale. Lo snapshot resta nel
      // ledger separato finché l'operatore non esegue la rettifica.
      statement:
        `DELETE FROM events_local
          WHERE id = ?
            AND rev IS NULL`,
      values: [event.client_uuid],
    },
    {
      statement: `DELETE FROM pending_ops WHERE id = ?`,
      values: [pending.id],
    },
  ], true);
  notifyChanged();
}

export async function listLegacyStockReconciliations(input: {
  tenantId: string;
  businessId?: string | null;
  includeResolved?: boolean;
}): Promise<LegacyStockReconciliation[]> {
  const db = await openLocalDb(null);
  const values: unknown[] = [input.tenantId];
  let where = 'tenant_id = ?';
  if (input.businessId) {
    where += ' AND (business_id = ? OR business_id IS NULL)';
    values.push(input.businessId);
  }
  if (!input.includeResolved) {
    where += ` AND status = 'reconciliation_required'`;
  }
  const result = await db.query(
    `SELECT client_uuid, pending_op_id, tenant_id, business_id, event_kind,
            event_snapshot_json, reason, status, resolution, created_at,
            updated_at, resolved_at
       FROM legacy_stock_reconciliations_v1
      WHERE ${where}
      ORDER BY created_at ASC`,
    values,
  );
  return (result.values ?? []).map((value) => {
    const row = value as Omit<LegacyStockReconciliation, 'event'> & {
      event_snapshot_json: string;
    };
    return {
      client_uuid: row.client_uuid,
      pending_op_id: Number(row.pending_op_id),
      tenant_id: row.tenant_id,
      business_id: row.business_id,
      event_kind: row.event_kind,
      event: JSON.parse(row.event_snapshot_json) as SyncEventInput,
      reason: row.reason,
      status: row.status,
      resolution: row.resolution,
      created_at: row.created_at,
      updated_at: row.updated_at,
      resolved_at: row.resolved_at,
    };
  });
}

export async function resolveLegacyStockReconciliation(input: {
  clientUuid: string;
  tenantId: string;
  resolvedAt?: string;
}): Promise<void> {
  const resolvedAt = input.resolvedAt ?? new Date().toISOString();
  const db = await openLocalDb(null);
  const result = await db.run(
    `UPDATE legacy_stock_reconciliations_v1
        SET status = 'resolved',
            resolution = 'physically_verified_and_reentered',
            resolved_at = ?,
            updated_at = ?
      WHERE client_uuid = ?
        AND tenant_id = ?
        AND status = 'reconciliation_required'`,
    [resolvedAt, resolvedAt, input.clientUuid, input.tenantId],
  );
  if ((result.changes?.changes ?? 0) !== 1) {
    throw new Error('legacy_stock_reconciliation_not_found');
  }
  notifyChanged();
}
